/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import { Box, Typography, Divider } from '@mui/material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import { formatPhoneNumber, displayPhoneNumber, formatAccountNumber, displayAccountNumber } from '../../utils/formatUtils';
import PhoneInput from '../shared/PhoneInput';
import { toast } from 'react-toastify';
import '../../styles/UserManagement.css';

const normalizeInstitutionRoleIds = (roles) => {
  if (!Array.isArray(roles) || !roles.length) return [];
  return roles
    .map((r) => (r && typeof r === 'object' && r._id != null ? r._id : r))
    .filter(Boolean);
};

const userHasAdminRole = (user, rolesCatalog) => {
  const list = user?.institution_roles;
  if (!list?.length) return false;
  return list.some((r) => {
    if (r && typeof r === 'object' && r.role) return r.role === 'Admin';
    const id = r && typeof r === 'object' ? r._id : r;
    const doc = rolesCatalog?.find((ir) => String(ir._id) === String(id));
    return doc?.role === 'Admin';
  });
};

const formatInstitutionRoleNames = (user, catalog) => {
  const list = user?.institution_roles;
  if (!list?.length) return '—';
  const names = list
    .map((r) => {
      if (r && typeof r === 'object' && r.role) return r.role;
      const id = r && typeof r === 'object' ? r._id : r;
      const doc = catalog?.find((ir) => String(ir._id) === String(id));
      return doc?.role;
    })
    .filter(Boolean);
  return names.length ? names.join(', ') : '—';
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [institutionRoles, setInstitutionRoles] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    contact_number: '',
    address: '',
    company: '',
    account_numbers: '',
    department: '',
    is_ncsu: false,
    facility: ''
  });
  const [facilities, setFacilities] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [facilityProjects, setFacilityProjects] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Manage Roles dialog state
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [rolesDialogUser, setRolesDialogUser] = useState(null);
  const [rolesSelection, setRolesSelection] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);

  // Email users state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const filteredUsers = users.filter(user => {
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatInstitutionRoleNames(user, institutionRoles).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredUsers,
    { defaultSortColumn: 'email', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchFacilities();
    fetchInstitutionRoles();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/users/current');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const isMainAdmin = (user) => user.email === 'admin@phytotron.com';

  const isCurrentUserMainAdmin = () => currentUser?.email === 'admin@phytotron.com';

  const isCurrentUserAdmin = () => userHasAdminRole(currentUser, institutionRoles);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/api/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  };

  const fetchInstitutionRoles = async () => {
    try {
      const response = await axios.get('/api/institution-roles');
      setInstitutionRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch institution roles:', error);
    }
  };

  const fetchProjectsForFacilities = async (facilityIds) => {
    setLoadingProjects(true);
    try {
      const projectsByFacility = {};
      for (const facilityId of facilityIds) {
        const response = await axios.get(`/api/projects/facility/${facilityId}`);
        const userProjects = response.data.filter(project => {
          const isUserInProject = project.users.some(user => 
            user.user === editingUser._id || user.email === editingUser.email
          );
          return isUserInProject && project.project_status === 'In Progress';
        });
        projectsByFacility[facilityId] = userProjects;
      }
      setFacilityProjects(projectsByFacility);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setLoadingProjects(false);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleEditClick = async (user) => {
    if (isMainAdmin(user) && !isCurrentUserMainAdmin()) {
      alert('Only the main admin can edit their own profile');
      return;
    }
    setEditingUser({
      ...user,
      department: user.department?._id || user.department,
      facility: user.facility?._id || user.facility || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const trimmedFirstName = (editingUser?.first_name || '').trim();
      const trimmedLastName = (editingUser?.last_name || '').trim();
      if (!trimmedFirstName || !trimmedLastName) {
        toast.error('First name and last name are required');
        return;
      }
      const accountNumbers = (editingUser?.account_numbers || [])
        .filter(acc => (typeof acc === 'object' ? acc?.number : acc)?.trim?.())
        .map(acc => (typeof acc === 'object' ? { number: (acc.number || '').trim(), source: acc.source || 'Manual' } : { number: String(acc).trim(), source: 'Manual' }));
      const fp = editingUser?.facility;
      const bc = editingUser?.billing_contact;
      const billingIsSelf = bc?.is_self !== false;
      if (!billingIsSelf && !(bc?.name || '').trim()) {
        toast.error('Billing person name is required');
        return;
      }
      const payload = {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        contact_number: editingUser?.contact_number ?? '',
        address: editingUser?.address ?? '',
        company: editingUser?.company ?? '',
        department: editingUser?.department || null,
        unity_id: editingUser?.unity_id,
        account_numbers: accountNumbers,
        facility: (typeof fp === 'string' && fp.trim() === '') || !fp ? null : fp,
        status: editingUser?.status,
        is_ncsu: editingUser?.is_ncsu,
        billing_contact: {
          is_self: billingIsSelf,
          name: billingIsSelf ? '' : (bc?.name || '').trim(),
          email: billingIsSelf ? '' : (bc?.email || '').trim(),
          contact_number: billingIsSelf ? '' : (bc?.contact_number || '').trim()
        }
      };
      await axios.put(`/api/users/${editingUser._id}`, payload);
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Failed to update user:', error);
      const errMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update user';
      toast.error(errMsg);
    }
  };

  const handleCreate = async () => {
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newUser.email)) {
        alert('Please enter a valid email address');
        return;
      }

      if (!newUser.first_name || !newUser.last_name || !newUser.password) {
        alert('Please fill in all required fields (First Name, Last Name, Password)');
        return;
      }

      const formattedUser = {
        email: newUser.email,
        password: newUser.password,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        contact_number: newUser.contact_number,
        address: newUser.address,
        company: newUser.company,
        account_numbers: newUser.account_numbers.split(',').map(num => num.trim()).filter(num => num),
        department: newUser.department || null,
        is_ncsu: newUser.is_ncsu,
        facility: newUser.facility || null,
        status: 'ACTIVE'
      };

      await axios.post('/api/users', formattedUser);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        contact_number: '',
        address: '',
        company: '',
        account_numbers: '',
        department: '',
        is_ncsu: false,
        facility: ''
      });
      fetchUsers();
      toast.success('User created successfully');
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  // ── Manage Roles handlers ──

  const handleManageRolesClick = (user) => {
    setRolesDialogUser(user);
    setRolesSelection(normalizeInstitutionRoleIds(user.institution_roles));
    setRolesDialogOpen(true);
  };

  const toggleRole = (roleId) => {
    setRolesSelection(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleRolesSave = async () => {
    setSavingRoles(true);
    try {
      await axios.put(`/api/users/${rolesDialogUser._id}`, {
        institution_roles: rolesSelection
      });
      setRolesDialogOpen(false);
      setRolesDialogUser(null);
      fetchUsers();
      toast.success('Roles updated successfully');
    } catch (error) {
      console.error('Failed to update roles:', error);
      toast.error(error.response?.data?.message || 'Failed to update roles');
    }
    setSavingRoles(false);
  };

  // ── Email handlers ──

  const toggleUserSelected = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const visibleUserIds = sortedData.map(u => u._id);
  const allVisibleSelected = visibleUserIds.length > 0 && visibleUserIds.every(id => selectedUserIds.includes(id));
  const someVisibleSelected = visibleUserIds.some(id => selectedUserIds.includes(id));

  const toggleSelectAllVisible = () => {
    setSelectedUserIds(prev =>
      allVisibleSelected
        ? prev.filter(id => !visibleUserIds.includes(id))
        : [...new Set([...prev, ...visibleUserIds])]
    );
  };

  const selectedUsers = users.filter(u => selectedUserIds.includes(u._id));

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSendingEmail(true);
    try {
      const { data } = await axios.post('/api/users/email', {
        userIds: selectedUserIds,
        subject: emailSubject,
        message: emailMessage
      });
      if (data.failed?.length) {
        toast.warning(`Sent ${data.sent} email(s); ${data.failed.length} failed: ${data.failed.map(f => f.email).join(', ')}`);
      } else {
        toast.success(`Sent ${data.sent} email(s)`);
      }
      if (data.sent > 0) {
        setEmailDialogOpen(false);
        setEmailSubject('');
        setEmailMessage('');
        setSelectedUserIds([]);
      }
    } catch (error) {
      console.error('Failed to send emails:', error);
      toast.error(error.response?.data?.message || 'Failed to send emails');
    }
    setSendingEmail(false);
  };

  const renderEmailDialog = () => (
    <Dialog
      open={emailDialogOpen}
      onClose={() => !sendingEmail && setEmailDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Email Users</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each recipient receives their own individual email. Recipients ({selectedUsers.length}):
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, maxHeight: 120, overflowY: 'auto' }}>
          {selectedUsers.map(u => (
            <Chip
              key={u._id}
              size="small"
              label={`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
              title={u.email}
              onDelete={sendingEmail ? undefined : () => toggleUserSelected(u._id)}
            />
          ))}
        </Box>
        <TextField
          label="Subject"
          fullWidth
          required
          margin="normal"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          disabled={sendingEmail}
        />
        <TextField
          label="Message"
          fullWidth
          required
          multiline
          minRows={8}
          margin="normal"
          value={emailMessage}
          onChange={(e) => setEmailMessage(e.target.value)}
          disabled={sendingEmail}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
        <Button
          onClick={handleSendEmail}
          variant="contained"
          color="primary"
          disabled={sendingEmail || selectedUsers.length === 0 || !emailSubject.trim() || !emailMessage.trim()}
        >
          {sendingEmail ? 'Sending...' : `Send to ${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Delete handlers ──

  const handleDeleteClick = (user) => {
    if (isMainAdmin(user)) {
      alert('The main admin cannot be deleted');
      return;
    }
    if (user.status === 'ACTIVE') {
      alert('Only inactive users can be deleted. Please set the user to inactive first.');
      return;
    }
    if (userHasAdminRole(user, institutionRoles) && !isCurrentUserMainAdmin()) {
      alert('Only the main admin can delete admin users');
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/users/${userToDelete._id}`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
      toast.success('User deleted successfully. Project history has been preserved.');
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.message || 'Failed to delete user');
    }
  };

  // ── View details ──

  const renderUserDetails = () => {
    if (!selectedUser) return null;

    return (
      <div className="user-details">
        <div className="detail-row">
          <strong>Email:</strong> 
          <span>{selectedUser.email}</span>
        </div>
        <div className="detail-row">
          <strong>First Name:</strong> 
          <span>{selectedUser.first_name}</span>
        </div>
        <div className="detail-row">
          <strong>Last Name:</strong> 
          <span>{selectedUser.last_name}</span>
        </div>
        <div className="detail-row">
          <strong>Contact Number:</strong> 
          <span>{displayPhoneNumber(selectedUser.contact_number)}</span>
        </div>
        <div className="detail-row">
          <strong>Address:</strong> 
          <span>{selectedUser.address}</span>
        </div>
        <div className="detail-row">
          <strong>Company:</strong> 
          <span>{selectedUser.company || 'None'}</span>
        </div>
        <div className="detail-row">
          <strong>Department:</strong> 
          <span>{selectedUser.department?.department_name || 'None'}</span>
        </div>
        <div className="detail-row">
          <strong>Status:</strong> 
          <span className={`status-badge ${selectedUser.status.toLowerCase()}`}>
            {selectedUser.status}
          </span>
        </div>
        <div className="detail-row">
          <strong>Associated with Institution:</strong> 
          <span>{selectedUser.is_ncsu ? 'Yes' : 'No'}</span>
        </div>
        {selectedUser.is_ncsu && (
          <div className="detail-row">
            <strong>Unity ID:</strong> 
            <span>{selectedUser.unity_id}</span>
          </div>
        )}
        <div className="detail-row">
          <strong>Account Information:</strong> 
          <div className="account-numbers-list">
            {selectedUser.account_numbers?.length > 0 ? (
              <ul>
                {selectedUser.account_numbers.map((account, index) => (
                  <li key={index}>
                    <strong>Account:</strong> {displayAccountNumber(account.number || account)} 
                    {account.source && <span className="account-source"> (Source: {account.source})</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <span>No account numbers</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Edit form (no roles here) ──

  const renderEditForm = () => (
    <Dialog 
      open={editDialogOpen} 
      onClose={() => setEditDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Edit User: {editingUser?.first_name} {editingUser?.last_name}
      </DialogTitle>
      <DialogContent>
        <div className="edit-form">
          <div className="user-info-section">
            <h3>Basic Information</h3>
            <TextField label="Email" fullWidth margin="normal" value={editingUser?.email || ''} disabled />
            <TextField
              label="First Name"
              fullWidth
              required
              margin="normal"
              value={editingUser?.first_name || ''}
              onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
              error={!editingUser?.first_name?.trim?.()}
              helperText={!editingUser?.first_name?.trim?.() ? 'First name is required' : ''}
            />
            <TextField
              label="Last Name"
              fullWidth
              required
              margin="normal"
              value={editingUser?.last_name || ''}
              onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
              error={!editingUser?.last_name?.trim?.()}
              helperText={!editingUser?.last_name?.trim?.() ? 'Last name is required' : ''}
            />
            <Box sx={{ mt: 2 }}>
              <InputLabel shrink>Contact Number</InputLabel>
              <PhoneInput
                value={editingUser?.contact_number || ''}
                onChange={(v) => setEditingUser({...editingUser, contact_number: v})}
              />
            </Box>
            <TextField
              label="Address"
              fullWidth
              margin="normal"
              value={editingUser?.address || ''}
              onChange={(e) => setEditingUser({...editingUser, address: e.target.value})}
            />
            <TextField
              label="Company/Organization"
              fullWidth
              margin="normal"
              value={editingUser?.company || ''}
              onChange={(e) => setEditingUser({...editingUser, company: e.target.value})}
            />
          </div>

          <div className="user-info-section">
            <h3>Department & Affiliation</h3>
            <FormControl fullWidth margin="normal">
              <InputLabel>Department</InputLabel>
              <Select
                value={editingUser?.department || ''}
                onChange={(e) => setEditingUser({...editingUser, department: e.target.value})}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept._id} value={dept._id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={editingUser?.is_ncsu || false}
                  onChange={(e) => setEditingUser({ ...editingUser, is_ncsu: e.target.checked })}
                />
              }
              label="Associated with the institution?"
              style={{ marginTop: '16px', marginBottom: '8px' }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Facility {userHasAdminRole(editingUser, institutionRoles) ? '(optional)' : ''}</InputLabel>
              <Select
                value={editingUser?.facility || ''}
                onChange={(e) => setEditingUser({...editingUser, facility: e.target.value})}
                label={`Facility ${userHasAdminRole(editingUser, institutionRoles) ? '(optional)' : ''}`}
              >
                <MenuItem value="">Not Disclosed</MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility._id} value={facility._id}>
                    {facility.facility_name}
                  </MenuItem>
                ))}
              </Select>
              {userHasAdminRole(editingUser, institutionRoles) && (
                <FormHelperText>Pre-selects this facility in filters across admin pages.</FormHelperText>
              )}
            </FormControl>
          </div>

          <div className="user-info-section">
            <h3>Account Information</h3>
            <div className="account-numbers-management">
              {editingUser?.account_numbers?.map((account, index) => (
                <div key={index} className="account-item">
                  <TextField
                    label="Account Number"
                    value={typeof account === 'object' ? account.number : account}
                    onChange={(e) => {
                      const formattedValue = formatAccountNumber(e.target.value);
                      const updatedAccounts = [...(editingUser.account_numbers || [])];
                      if (typeof account === 'object') {
                        updatedAccounts[index] = { ...account, number: formattedValue };
                      } else {
                        updatedAccounts[index] = { number: formattedValue, source: 'Manual' };
                      }
                      setEditingUser({...editingUser, account_numbers: updatedAccounts});
                    }}
                    placeholder="XXXXXX-XXXXX"
                    inputProps={{ maxLength: 12 }}
                    style={{ marginRight: '10px', width: '200px' }}
                  />
                  <TextField
                    label="Source"
                    value={typeof account === 'object' ? account.source : 'Manual'}
                    onChange={(e) => {
                      const updatedAccounts = [...(editingUser.account_numbers || [])];
                      if (typeof account === 'object') {
                        updatedAccounts[index] = { ...account, source: e.target.value };
                      } else {
                        updatedAccounts[index] = { number: account, source: e.target.value };
                      }
                      setEditingUser({...editingUser, account_numbers: updatedAccounts});
                    }}
                    style={{ marginRight: '10px', width: '150px' }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const updatedAccounts = editingUser.account_numbers.filter((_, i) => i !== index);
                      setEditingUser({...editingUser, account_numbers: updatedAccounts});
                    }}
                    style={{ height: '56px' }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  const updatedAccounts = [...(editingUser.account_numbers || []), { number: '', source: 'Manual' }];
                  setEditingUser({...editingUser, account_numbers: updatedAccounts});
                }}
                style={{ marginTop: '10px' }}
              >
                Add Account Number
              </Button>
            </div>
          </div>

          <div className="user-info-section">
            <h3>Billing Person (Invoice To)</h3>
            <FormHelperText style={{ marginBottom: '8px' }}>
              Listed on the billing request sent to the business office so they have someone to
              contact about this user's invoice payments. Takes no part in the invoicing process.
            </FormHelperText>
            <FormControlLabel
              control={
                <Switch
                  checked={editingUser?.billing_contact?.is_self !== false}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    billing_contact: e.target.checked
                      ? { is_self: true, name: '', email: '', contact_number: '' }
                      : { ...(editingUser.billing_contact || {}), is_self: false }
                  })}
                  color="primary"
                />
              }
              label="This user is their own billing person"
            />
            {editingUser?.billing_contact?.is_self === false && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                <TextField
                  label="Billing Person Name"
                  value={editingUser?.billing_contact?.name || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    billing_contact: { ...editingUser.billing_contact, is_self: false, name: e.target.value }
                  })}
                  style={{ width: '220px' }}
                />
                <TextField
                  label="Billing Person Email"
                  value={editingUser?.billing_contact?.email || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    billing_contact: { ...editingUser.billing_contact, is_self: false, email: e.target.value }
                  })}
                  style={{ width: '240px' }}
                />
                <Box sx={{ minWidth: 220 }}>
                  <InputLabel shrink>Billing Person Phone</InputLabel>
                  <PhoneInput
                    value={editingUser?.billing_contact?.contact_number || ''}
                    onChange={(v) => setEditingUser({
                      ...editingUser,
                      billing_contact: {
                        ...editingUser.billing_contact,
                        is_self: false,
                        contact_number: v
                      }
                    })}
                  />
                </Box>
              </div>
            )}
          </div>

          <div className="user-info-section">
            <h3>Account Status</h3>
            <FormControl fullWidth margin="normal">
              <FormControlLabel
                control={
                  <Switch
                    checked={editingUser?.status === 'ACTIVE'}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      status: e.target.checked ? 'ACTIVE' : 'INACTIVE'
                    })}
                    color="primary"
                  />
                }
                label={`User Status: ${editingUser?.status === 'ACTIVE' ? 'Active' : 'Inactive'}`}
              />
              <FormHelperText>
                {editingUser?.status === 'ACTIVE' 
                  ? 'User can access the system and their projects'
                  : 'User cannot access the system but their project history is preserved'}
              </FormHelperText>
            </FormControl>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Close</Button>
        <Button onClick={handleEditSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Create form (no roles here) ──

  const renderCreateForm = () => (
    <Dialog 
      open={createDialogOpen} 
      onClose={() => setCreateDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create New User</DialogTitle>
      <DialogContent>
        <div className="edit-form">
          <TextField label="Email" type="email" fullWidth required margin="normal" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          <TextField label="Password" type="password" fullWidth required margin="normal" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
          <TextField label="First Name" fullWidth required margin="normal" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} />
          <TextField label="Last Name" fullWidth required margin="normal" value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} />
          <Box sx={{ mt: 2 }}>
            <InputLabel shrink>Contact Number</InputLabel>
            <PhoneInput
              value={newUser.contact_number}
              onChange={(v) => setNewUser({ ...newUser, contact_number: v })}
              required
            />
          </Box>
          <TextField label="Address" fullWidth required margin="normal" value={newUser.address} onChange={(e) => setNewUser({ ...newUser, address: e.target.value })} />
          <TextField label="Company/Organization" fullWidth margin="normal" value={newUser.company} onChange={(e) => setNewUser({ ...newUser, company: e.target.value })} />
          <TextField
            label="Account Numbers"
            fullWidth
            margin="normal"
            value={newUser.account_numbers}
            onChange={(e) => setNewUser({ ...newUser, account_numbers: e.target.value })}
            helperText="Separate multiple account numbers with commas"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Department</InputLabel>
            <Select
              value={newUser.department}
              onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
            >
              {departments.map((dept) => (
                <MenuItem key={dept._id} value={dept._id}>
                  {dept.department_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={newUser.is_ncsu}
                onChange={(e) => setNewUser({ ...newUser, is_ncsu: e.target.checked })}
              />
            }
            label="Associated with the institution?"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Manage Roles dialog ──

  const renderManageRolesDialog = () => {
    if (!rolesDialogUser) return null;

    const currentRoleIds = normalizeInstitutionRoleIds(rolesDialogUser.institution_roles);
    const hasChanges = JSON.stringify([...rolesSelection].sort()) !== JSON.stringify([...currentRoleIds].sort());

    return (
      <Dialog
        open={rolesDialogOpen}
        onClose={() => setRolesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Roles
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rolesDialogUser.first_name} {rolesDialogUser.last_name} ({rolesDialogUser.email})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {institutionRoles.map((role) => {
              const isSelected = rolesSelection.includes(role._id);
              return (
                <Box
                  key={role._id}
                  onClick={() => toggleRole(role._id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isSelected ? 'primary.50' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: isSelected ? 'primary.50' : 'action.hover',
                    }
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0 }}
                    tabIndex={-1}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={isSelected ? 600 : 400}>
                        {role.role}
                      </Typography>
                      {role.is_protected && (
                        <Chip label="Platform" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {role.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {rolesSelection.length === 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              No roles selected. The user will still have basic access but no specific role designation.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolesDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRolesSave}
            variant="contained"
            color="primary"
            disabled={!hasChanges || savingRoles}
          >
            {savingRoles ? 'Saving...' : 'Save Roles'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ── Actions column ──

  const renderUserActions = (user) => {
    const isUserAdmin = userHasAdminRole(user, institutionRoles);
    const canEdit = isMainAdmin(user) ? isCurrentUserMainAdmin() : true;
    const canDelete = user.status === 'INACTIVE' && !isMainAdmin(user) && 
                     (!isUserAdmin || isCurrentUserMainAdmin());

    return (
      <td className="action-buttons">
        <Button
          variant="contained"
          size="small"
          className="row-action-edit"
          style={{ marginRight: '6px' }}
          onClick={() => handleEditClick(user)}
          disabled={!canEdit}
        >
          View & Edit
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          style={{ marginRight: '6px' }}
          onClick={() => handleManageRolesClick(user)}
        >
          Manage Roles
        </Button>
        {canDelete && (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDeleteClick(user)}
          >
            Delete
          </Button>
        )}
      </td>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="user-management">
      <h2>User Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Users"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or role..."
          sx={{ minWidth: 300 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="ALL">All Users</MenuItem>
            <MenuItem value="ACTIVE">Active Users</MenuItem>
            <MenuItem value="INACTIVE">Inactive Users</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New User
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setEmailDialogOpen(true)}
          disabled={selectedUserIds.length === 0}
        >
          Email Selected{selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}
        </Button>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <th className="select-column">
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                onChange={toggleSelectAllVisible}
                inputProps={{ 'aria-label': 'Select all users' }}
              />
            </th>
            <SortableTableHeader column="email" label="Email" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="first_name" label="First Name" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="last_name" label="Last Name" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Roles</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(user => (
            <tr key={user._id} className={user.status === 'INACTIVE' ? 'inactive-user' : ''}>
              <td className="select-column">
                <Checkbox
                  size="small"
                  checked={selectedUserIds.includes(user._id)}
                  onChange={() => toggleUserSelected(user._id)}
                  inputProps={{ 'aria-label': `Select ${user.email}` }}
                />
              </td>
              <td>{user.email}</td>
              <td>{user.first_name}</td>
              <td>{user.last_name}</td>
              <td>{formatInstitutionRoleNames(user, institutionRoles)}</td>
              <td>
                <span className={`status-badge ${user.status.toLowerCase()}`}>
                  {user.status}
                </span>
              </td>
              {renderUserActions(user)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* View details dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          User Details: {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          {renderUserDetails()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {renderEditForm()}
      {renderCreateForm()}
      {renderManageRolesDialog()}
      {renderEmailDialog()}

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: 'var(--ui-danger-fg-strong)' }}>
          Delete User - Permanent Action
        </DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: '16px' }}>
            <strong>Are you sure you want to permanently delete this user?</strong>
          </div>
          {userToDelete && (
            <div style={{ marginBottom: '16px' }}>
              <div><strong>User:</strong> {userToDelete.first_name} {userToDelete.last_name}</div>
              <div><strong>Email:</strong> {userToDelete.email}</div>
              <div><strong>Status:</strong> {userToDelete.status}</div>
            </div>
          )}
          <div style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <strong>Warning:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>This action cannot be undone</li>
              <li>The user will be permanently removed from the system</li>
              <li>Project history will be preserved</li>
            </ul>
          </div>
          <div style={{ 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            padding: '12px', 
            borderRadius: '4px',
            color: '#721c24'
          }}>
            <strong>This is a permanent deletion.</strong> Only proceed if you are absolutely certain.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Permanently Delete User
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement;
